/* ============================================================================
 * ATP Engine Mega Menu — v2 "live data"
 * ----------------------------------------------------------------------------
 * What changed vs v1:
 *   - Product tiles no longer trust the menu JSON for title / price / image /
 *     availability. The JSON only decides WHICH products appear WHERE.
 *     When a part type is opened, each visible tile is hydrated from
 *     Shopify's live endpoint:  /products/<handle>.js
 *     so tiles always match Shopify Admin (title, price, compare-at,
 *     featured image, sold-out state, current URL).
 *   - Works with BOTH schemas: the slim v2 files ({handle, id}) and the old
 *     v1 files (full snapshots). Old snapshot fields are used only as an
 *     offline fallback and NEVER for availability — so stale
 *     "available:false" flags can no longer mark live products as sold out.
 *   - Products that 404 (deleted, unpublished, or handle changed) are removed
 *     from the grid automatically and logged to the console for cleanup.
 *   - Caching: in-memory + sessionStorage (15 min TTL). Max 5 concurrent
 *     product requests; duplicate requests are coalesced; requests for tiles
 *     that left the DOM are skipped.
 *
 * Interaction model is unchanged from v1: click-only open, Escape closes,
 * outside click closes, arrow-key navigation across manufacturer triggers,
 * mobile Manufacturer -> Model -> Component -> Products with back button.
 * ========================================================================== */
(function () {
  'use strict';

  if (window.__atpEngineMegaMenuLoaded) return;
  window.__atpEngineMegaMenuLoaded = true;

  /* ------------------------------------------------------------------ */
  /* Tunables                                                            */
  /* ------------------------------------------------------------------ */
  var desktopPageSize = 12;          // product tiles per "page" on desktop
  var mobilePageSize = 10;           // product tiles per "page" on mobile
  var MAX_CONCURRENT = 5;            // simultaneous /products/*.js requests
  var EAGER_IMAGES = 4;              // first N images load eagerly per grid
  var PRODUCT_TTL_MS = 15 * 60 * 1000; // sessionStorage freshness window
  var SESSION_PREFIX = 'atpemm:v2:'; // sessionStorage key prefix

  /* ------------------------------------------------------------------ */
  /* State                                                               */
  /* ------------------------------------------------------------------ */
  var dataCache = new Map();         // manufacturer JSON url -> Promise(data)
  var productCache = new Map();      // handle -> {ok:true,data} | {ok:false,status:404}
  var productInflight = new Map();   // handle -> Promise(result)
  var fetchQueue = [];               // pending hydration jobs
  var activeFetches = 0;
  var gridStates = new WeakMap();    // grid element -> {pending:int}
  var desktopStates = new WeakMap();
  var mobileStates = new WeakMap();
  var activeDesktopRoot = null;
  var warnedHandles = {};            // handle -> true (console noise control)

  /* ------------------------------------------------------------------ */
  /* Small DOM helpers                                                   */
  /* ------------------------------------------------------------------ */
  function create(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = text;
    return node;
  }

  function empty(node) {
    while (node && node.firstChild) node.removeChild(node.firstChild);
  }

  /* ------------------------------------------------------------------ */
  /* Money — uses the theme's own money format when available            */
  /* ------------------------------------------------------------------ */
  function formatWithDelimiters(cents, precision, thousands, decimal) {
    precision = precision === undefined ? 2 : precision;
    thousands = thousands || ',';
    decimal = decimal || '.';
    var fixed = (Number(cents) / 100).toFixed(precision);
    var parts = fixed.split('.');
    var whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
    return parts[1] ? whole + decimal + parts[1] : whole;
  }

  function formatMoney(cents, currencyFallback) {
    if (cents === null || cents === undefined || cents === '') return '';
    var value = Number(cents);
    if (!isFinite(value)) return '';

    var format = window.theme && window.theme.currency && window.theme.currency.format;
    if (format && format.indexOf('{{') > -1) {
      var match = /\{\{\s*(\w+)\s*\}\}/.exec(format);
      var key = match ? match[1] : 'amount';
      var amount;
      switch (key) {
        case 'amount_no_decimals': amount = formatWithDelimiters(value, 0); break;
        case 'amount_with_comma_separator': amount = formatWithDelimiters(value, 2, '.', ','); break;
        case 'amount_no_decimals_with_comma_separator': amount = formatWithDelimiters(value, 0, '.', ','); break;
        case 'amount_with_apostrophe_separator': amount = formatWithDelimiters(value, 2, "'", '.'); break;
        case 'amount_no_decimals_with_space_separator': amount = formatWithDelimiters(value, 0, ' '); break;
        case 'amount_with_space_separator': amount = formatWithDelimiters(value, 2, ' ', ','); break;
        case 'amount_with_period_and_space_separator': amount = formatWithDelimiters(value, 2, ' ', '.'); break;
        default: amount = formatWithDelimiters(value, 2);
      }
      return format.replace(/\{\{\s*\w+\s*\}\}/, amount);
    }

    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: String(currencyFallback || 'USD').toUpperCase(),
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value / 100);
    } catch (error) {
      return '$' + (value / 100).toFixed(2);
    }
  }

  /* ------------------------------------------------------------------ */
  /* URL + handle helpers                                                */
  /* ------------------------------------------------------------------ */
  function rootUrl() {
    var root = (window.theme && window.theme.routes && window.theme.routes.rootUrl) || '/';
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return root;
  }

  function placementHandle(placement) {
    if (!placement) return '';
    if (placement.handle) return String(placement.handle);
    var url = placement.url || '';
    var match = /\/products\/([^\/?#]+)/.exec(url);
    if (!match) return '';
    try { return decodeURIComponent(match[1]); } catch (error) { return match[1]; }
  }

  function productJsUrl(handle) {
    return rootUrl() + 'products/' + encodeURIComponent(handle) + '.js';
  }

  function productPageUrl(handle) {
    return rootUrl() + 'products/' + encodeURIComponent(handle);
  }

  /* "4359489-genuine-cummins-isx15-fuel-pump" -> "4359489 Genuine Cummins Isx15 Fuel Pump"
     Only used as a last-resort label when live data cannot be fetched. */
  function prettyHandle(handle) {
    return String(handle || '')
      .split('-')
      .filter(Boolean)
      .map(function (word) { return word.charAt(0).toUpperCase() + word.slice(1); })
      .join(' ');
  }

  function sizedImage(url, width) {
    if (!url) return '';
    if (!/^https?:\/\//i.test(url)) return url;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  /* ------------------------------------------------------------------ */
  /* sessionStorage product cache (survives page navigation)             */
  /* ------------------------------------------------------------------ */
  function sessionGet(handle) {
    try {
      var raw = window.sessionStorage.getItem(SESSION_PREFIX + handle);
      if (!raw) return null;
      var entry = JSON.parse(raw);
      if (!entry || !entry.ts || (Date.now() - entry.ts) > PRODUCT_TTL_MS) return null;
      return entry.d || null;
    } catch (error) { return null; }
  }

  function sessionSet(handle, data) {
    try {
      window.sessionStorage.setItem(SESSION_PREFIX + handle, JSON.stringify({ ts: Date.now(), d: data }));
    } catch (error) { /* storage full or unavailable — memory cache still works */ }
  }

  /* ------------------------------------------------------------------ */
  /* Live product fetching (queued, capped, deduplicated)                */
  /* ------------------------------------------------------------------ */
  function slimProduct(raw, handle) {
    var compareAt = raw.compare_at_price;
    if (compareAt === null || compareAt === undefined) compareAt = raw.compare_at_price_min;
    return {
      t: raw.title || prettyHandle(handle),
      u: raw.url || productPageUrl(handle),
      p: raw.price,
      cp: compareAt,
      pv: !!raw.price_varies,
      av: raw.available !== false,
      img: raw.featured_image || (raw.images && raw.images[0]) || ''
    };
  }

  function fetchProduct(handle) {
    if (productCache.has(handle)) return Promise.resolve(productCache.get(handle));

    var fromSession = sessionGet(handle);
    if (fromSession) {
      var hit = { ok: true, data: fromSession };
      productCache.set(handle, hit);
      return Promise.resolve(hit);
    }

    if (productInflight.has(handle)) return productInflight.get(handle);

    var request = fetch(productJsUrl(handle), { credentials: 'same-origin' })
      .then(function (response) {
        if (response.status === 404 || response.status === 410) {
          var missing = { ok: false, status: 404 };
          productCache.set(handle, missing); // unpublished/deleted: no point retrying this session
          return missing;
        }
        if (!response.ok) return { ok: false, status: response.status };
        return response.json().then(function (raw) {
          var result = { ok: true, data: slimProduct(raw, handle) };
          productCache.set(handle, result);
          sessionSet(handle, result.data);
          return result;
        });
      })
      .catch(function () { return { ok: false, status: 0 }; })
      .then(function (result) {
        productInflight.delete(handle);
        return result;
      });

    productInflight.set(handle, request);
    return request;
  }

  function pumpQueue() {
    while (activeFetches < MAX_CONCURRENT && fetchQueue.length) {
      var job = fetchQueue.shift();
      /* The user moved on (grid was re-rendered): skip the request entirely. */
      if (!job.card.isConnected) { settleGrid(job.grid); continue; }
      activeFetches++;
      (function (currentJob) {
        fetchProduct(currentJob.handle).then(function (result) {
          applyResult(currentJob, result);
        }).catch(function () {
          applyResult(currentJob, { ok: false, status: 0 });
        }).then(function () {
          activeFetches--;
          settleGrid(currentJob.grid);
          pumpQueue();
        });
      })(job);
    }
  }

  /* ------------------------------------------------------------------ */
  /* Grid bookkeeping — show a friendly note if every tile was removed   */
  /* ------------------------------------------------------------------ */
  function gridState(grid) {
    var state = gridStates.get(grid);
    if (!state) { state = { pending: 0 }; gridStates.set(grid, state); }
    return state;
  }

  function settleGrid(grid) {
    if (!grid) return;
    var state = gridState(grid);
    state.pending = Math.max(0, state.pending - 1);
    maybeMarkGridEmpty(grid);
  }

  function maybeMarkGridEmpty(grid) {
    if (!grid || !grid.isConnected) return;
    var state = gridState(grid);
    if (state.pending > 0) return;
    if (grid.querySelector('.atp-emm-product-card, .atp-emm-mobile-product-card')) return;
    if (grid.querySelector('.atp-emm-no-products')) return;
    grid.appendChild(create('div', 'atp-emm-no-products',
      'The products in this part type are no longer published. Update this section in the Engine Menu Manager.'));
  }

  /* ------------------------------------------------------------------ */
  /* Product cards                                                       */
  /* ------------------------------------------------------------------ */
  function fillCardContent(card, product, options) {
    empty(card);
    card.classList.remove('is-loading');
    card.classList.toggle('is-unavailable', product.available === false);

    var imageLink = create('a', 'atp-emm-product-image');
    imageLink.href = product.url || '#';
    imageLink.setAttribute('aria-label', product.title || 'View product');

    var imageSrc = product.imageSrc || options.placeholder;
    var image = create('img');
    image.src = sizedImage(imageSrc, options.mobile ? 180 : 360);
    if (imageSrc && /^https?:\/\//i.test(imageSrc)) {
      image.srcset =
        sizedImage(imageSrc, options.mobile ? 180 : 260) + ' ' + (options.mobile ? 180 : 260) + 'w, ' +
        sizedImage(imageSrc, options.mobile ? 300 : 420) + ' ' + (options.mobile ? 300 : 420) + 'w';
      image.sizes = options.mobile ? '88px' : '(max-width: 1280px) 160px, 210px';
    }
    image.alt = product.imageAlt || product.title || '';
    image.loading = options.eager ? 'eager' : 'lazy';
    image.decoding = 'async';
    imageLink.appendChild(image);

    var info = create('div', 'atp-emm-product-info');
    var title = create('a', 'atp-emm-product-title', product.title || 'View product');
    title.href = product.url || '#';
    info.appendChild(title);

    var priceRow = create('div', 'atp-emm-price-row');
    var priceText = formatMoney(product.price, options.currency);
    if (priceText) {
      priceRow.appendChild(create('span', 'atp-emm-price', product.priceVaries ? 'From ' + priceText : priceText));
    }
    if (product.compareAt && Number(product.compareAt) > Number(product.price || 0)) {
      priceRow.appendChild(create('span', 'atp-emm-compare-price', formatMoney(product.compareAt, options.currency)));
    }
    if (priceRow.childNodes.length) info.appendChild(priceRow);

    /* Sold-out is only ever shown from LIVE data (product.available === false).
       Fallback renders pass available === null and never claim sold out. */
    if (product.available === false) info.appendChild(create('span', 'atp-emm-sold-out', 'Sold out'));

    card.appendChild(imageLink);
    card.appendChild(info);
  }

  function applyResult(job, result) {
    var card = job.card;
    if (!card.isConnected) return;

    if (result.ok) {
      var data = result.data;
      fillCardContent(card, {
        title: data.t,
        url: data.u,
        price: data.p,
        compareAt: data.cp,
        priceVaries: data.pv,
        available: data.av,
        imageSrc: data.img,
        imageAlt: data.t
      }, job.options);
      return;
    }

    if (result.status === 404) {
      /* Deleted, unpublished, or the handle changed — remove the tile. */
      if (!warnedHandles[job.handle]) {
        warnedHandles[job.handle] = true;
        if (window.console && console.warn) {
          console.warn('[ATP engine menu] Product not found, tile hidden: "' + job.handle +
            '". Remove or re-add it in the Engine Menu Manager.');
        }
      }
      var grid = card.parentNode;
      card.parentNode.removeChild(card);
      maybeMarkGridEmpty(grid);
      return;
    }

    /* Network / server hiccup: fall back to whatever the placement carries
       (old v1 files still have snapshots) without claiming availability. */
    var placement = job.placement || {};
    fillCardContent(card, {
      title: placement.title || prettyHandle(job.handle),
      url: placement.url || productPageUrl(job.handle),
      price: placement.price,
      compareAt: placement.compareAtPrice,
      priceVaries: false,
      available: null,
      imageSrc: (placement.image && placement.image.src) || '',
      imageAlt: (placement.image && placement.image.alt) || ''
    }, job.options);
  }

  function productCard(placement, options) {
    var card = create('article', options.mobile ? 'atp-emm-mobile-product-card' : 'atp-emm-product-card');
    var handle = placementHandle(placement);

    if (!handle) {
      /* No handle and no URL — nothing to link to; render from snapshot only. */
      fillCardContent(card, {
        title: placement.title || 'View product',
        url: placement.url || '#',
        price: placement.price,
        compareAt: placement.compareAtPrice,
        priceVaries: false,
        available: null,
        imageSrc: (placement.image && placement.image.src) || '',
        imageAlt: ''
      }, options);
      return card;
    }

    var cached = productCache.get(handle);
    if (cached) {
      /* Instant render from cache — no skeleton flash. (Fill directly:
         the card isn't in the DOM yet, so applyResult's isConnected
         guard would skip it.) */
      if (cached.ok === false && cached.status === 404) return null; // suppress known-dead tiles entirely
      if (cached.ok) {
        var live = cached.data;
        fillCardContent(card, {
          title: live.t,
          url: live.u,
          price: live.p,
          compareAt: live.cp,
          priceVaries: live.pv,
          available: live.av,
          imageSrc: live.img,
          imageAlt: live.t
        }, options);
        return card;
      }
      /* Any other cached shape falls through to the normal queued path. */
    }

    /* Skeleton shell while the live request runs. */
    card.classList.add('is-loading');
    var imageBox = create('span', 'atp-emm-product-image');
    imageBox.appendChild(create('span', 'atp-emm-skel atp-emm-skel-img'));
    var info = create('div', 'atp-emm-product-info');
    info.appendChild(create('span', 'atp-emm-skel atp-emm-skel-line'));
    info.appendChild(create('span', 'atp-emm-skel atp-emm-skel-line atp-emm-skel-line--short'));
    card.appendChild(imageBox);
    card.appendChild(info);

    gridState(options.grid).pending++;
    fetchQueue.push({ card: card, handle: handle, placement: placement, options: options, grid: options.grid });
    return card;
  }

  function renderProductList(grid, products, baseOptions) {
    gridStates.set(grid, { pending: 0 });
    products.forEach(function (placement, index) {
      var card = productCard(placement, {
        currency: baseOptions.currency,
        placeholder: baseOptions.placeholder,
        mobile: baseOptions.mobile,
        eager: index < EAGER_IMAGES,
        grid: grid
      });
      if (card) grid.appendChild(card);
    });
    pumpQueue();
    maybeMarkGridEmpty(grid);
  }

  /* ------------------------------------------------------------------ */
  /* Manufacturer JSON loading                                           */
  /* ------------------------------------------------------------------ */
  function loadData(url) {
    if (!url) return Promise.reject(new Error('Missing menu data URL.'));
    if (dataCache.has(url)) return dataCache.get(url);

    var request = fetch(url, { credentials: 'same-origin', cache: 'force-cache' })
      .then(function (response) {
        if (!response.ok) throw new Error('Menu data request failed (' + response.status + ').');
        return response.json();
      })
      .catch(function (error) {
        dataCache.delete(url);
        throw error;
      });

    dataCache.set(url, request);
    return request;
  }

  /* ------------------------------------------------------------------ */
  /* Shared data helpers                                                 */
  /* ------------------------------------------------------------------ */
  function countProducts(model) {
    if (!model || !Array.isArray(model.components)) return 0;
    return model.components.reduce(function (total, component) {
      return total + (Array.isArray(component.products) ? component.products.length : 0);
    }, 0);
  }

  function firstModelIndex(data) {
    if (!data || !Array.isArray(data.models) || !data.models.length) return 0;
    var index = data.models.findIndex(function (model) { return countProducts(model) > 0; });
    return index > -1 ? index : 0;
  }

  function firstComponentIndex(model) {
    if (!model || !Array.isArray(model.components) || !model.components.length) return 0;
    var index = model.components.findIndex(function (component) {
      return Array.isArray(component.products) && component.products.length > 0;
    });
    return index > -1 ? index : 0;
  }

  function searchUrl(manufacturer, model, component) {
    var query = [manufacturer, model, component].filter(Boolean).join(' ');
    return '/search?type=product&q=' + encodeURIComponent(query);
  }

  /* ------------------------------------------------------------------ */
  /* Desktop                                                             */
  /* ------------------------------------------------------------------ */
  function setDesktopStatus(root, message, loading) {
    var status = root.querySelector('[data-atp-status]');
    var body = root.querySelector('[data-atp-body]');
    if (!status || !body) return;
    status.hidden = false;
    status.classList.toggle('is-loading', !!loading);
    status.textContent = message || '';
    body.hidden = true;
  }

  function showDesktopBody(root) {
    var status = root.querySelector('[data-atp-status]');
    var body = root.querySelector('[data-atp-body]');
    if (status) {
      status.hidden = true;
      status.classList.remove('is-loading');
    }
    if (body) body.hidden = false;
  }

  function positionDesktop(root) {
    if (!root || root !== activeDesktopRoot) return;
    var anchor = root.closest('.header-content_bottom') || root.closest('.header-desktop') || root;
    var rect = anchor.getBoundingClientRect();
    root.style.setProperty('--atp-emm-top', Math.max(0, Math.round(rect.bottom)) + 'px');
  }

  function closeDesktop(root, restoreFocus) {
    root = root || activeDesktopRoot;
    if (!root) return;
    var flyout = root.querySelector('[data-atp-flyout]');
    var overlay = root.querySelector('.atp-emm-overlay');
    var activeTrigger = root.querySelector('.atp-emm-trigger.is-active');
    root.querySelectorAll('.atp-emm-trigger').forEach(function (trigger) {
      trigger.classList.remove('is-active');
      trigger.setAttribute('aria-expanded', 'false');
    });
    if (flyout) flyout.hidden = true;
    if (overlay) overlay.hidden = true;
    document.documentElement.classList.remove('atp-emm-is-open');
    if (activeDesktopRoot === root) activeDesktopRoot = null;
    if (restoreFocus && activeTrigger) activeTrigger.focus();
  }

  function openDesktop(trigger) {
    var root = trigger.closest('[data-atp-emm-desktop]');
    if (!root) return;
    if (activeDesktopRoot && activeDesktopRoot !== root) closeDesktop(activeDesktopRoot, false);

    var flyout = root.querySelector('[data-atp-flyout]');
    var overlay = root.querySelector('.atp-emm-overlay');
    var slug = trigger.getAttribute('data-atp-manufacturer');
    var source = trigger.getAttribute('data-atp-source');
    var label = trigger.getAttribute('data-atp-label') || trigger.textContent.trim();
    var existing = desktopStates.get(root) || {};

    root.querySelectorAll('.atp-emm-trigger').forEach(function (item) {
      var active = item === trigger;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-expanded', active ? 'true' : 'false');
    });

    activeDesktopRoot = root;
    document.documentElement.classList.add('atp-emm-is-open');
    positionDesktop(root);
    if (flyout) flyout.hidden = false;
    if (overlay) overlay.hidden = false;
    var heading = root.querySelector('[data-atp-heading]');
    if (heading) heading.textContent = label;

    if (existing.slug === slug && existing.data) {
      desktopStates.set(root, existing);
      renderDesktop(root);
      return;
    }

    setDesktopStatus(root, 'Loading ' + label + ' engine parts…', true);
    desktopStates.set(root, {
      slug: slug,
      label: label,
      source: source,
      data: null,
      modelIndex: 0,
      componentIndex: 0,
      filter: '',
      limit: desktopPageSize
    });

    loadData(source).then(function (data) {
      var state = desktopStates.get(root);
      if (!state || state.slug !== slug) return;
      state.data = data;
      state.modelIndex = firstModelIndex(data);
      state.componentIndex = firstComponentIndex(data.models[state.modelIndex]);
      state.filter = '';
      state.limit = desktopPageSize;
      desktopStates.set(root, state);
      var filter = root.querySelector('[data-atp-filter]');
      if (filter) filter.value = '';
      renderDesktop(root);
    }).catch(function () {
      var state = desktopStates.get(root);
      if (!state || state.slug !== slug) return;
      setDesktopStatus(root, 'This menu could not be loaded. Please refresh the page or use site search.', false);
    });
  }

  function renderDesktop(root) {
    var state = desktopStates.get(root);
    if (!state || !state.data || !Array.isArray(state.data.models)) return;
    var data = state.data;
    var model = data.models[state.modelIndex] || data.models[0];
    if (!model) {
      setDesktopStatus(root, 'No engine models are available for this manufacturer.', false);
      return;
    }

    if (state.modelIndex >= data.models.length) state.modelIndex = 0;
    if (!Array.isArray(model.components)) model.components = [];
    if (state.componentIndex >= model.components.length) state.componentIndex = firstComponentIndex(model);

    showDesktopBody(root);
    renderDesktopModels(root, state);
    renderDesktopComponents(root, state);
    renderDesktopProducts(root, state);
  }

  function renderDesktopModels(root, state) {
    var list = root.querySelector('[data-atp-model-list]');
    if (!list) return;
    empty(list);
    state.data.models.forEach(function (model, index) {
      var button = create('button', 'atp-emm-model-button' + (index === state.modelIndex ? ' is-active' : ''));
      button.type = 'button';
      button.setAttribute('data-atp-model-index', String(index));
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-selected', index === state.modelIndex ? 'true' : 'false');
      button.appendChild(create('span', '', model.title));
      button.appendChild(create('span', 'atp-emm-count', String(countProducts(model))));
      list.appendChild(button);
    });
  }

  function filteredComponents(state) {
    var model = state.data.models[state.modelIndex];
    var components = model && Array.isArray(model.components) ? model.components : [];
    var term = String(state.filter || '').trim().toLowerCase();
    return components.map(function (component, index) {
      return { component: component, index: index };
    }).filter(function (entry) {
      return !term || String(entry.component.title || '').toLowerCase().indexOf(term) > -1;
    });
  }

  function renderDesktopComponents(root, state) {
    var model = state.data.models[state.modelIndex];
    var modelName = root.querySelector('[data-atp-model-name]');
    var list = root.querySelector('[data-atp-component-list]');
    if (modelName) modelName.textContent = model ? model.title : '';
    if (!list) return;
    empty(list);

    var entries = filteredComponents(state);
    if (!entries.length) {
      list.appendChild(create('div', 'atp-emm-empty-filter', 'No part types match this filter.'));
      return;
    }

    entries.forEach(function (entry) {
      var component = entry.component;
      var active = entry.index === state.componentIndex;
      var button = create('button', 'atp-emm-component-button' + (active ? ' is-active' : ''));
      button.type = 'button';
      button.setAttribute('data-atp-component-index', String(entry.index));
      button.appendChild(create('span', '', component.title));
      button.appendChild(create('span', 'atp-emm-count', String((component.products || []).length)));
      list.appendChild(button);
    });
  }

  function renderDesktopProducts(root, state) {
    var model = state.data.models[state.modelIndex];
    var component = model && model.components ? model.components[state.componentIndex] : null;
    var breadcrumb = root.querySelector('[data-atp-breadcrumb]');
    var heading = root.querySelector('[data-atp-component-name]');
    var grid = root.querySelector('[data-atp-product-grid]');
    var viewAll = root.querySelector('[data-atp-view-all]');
    var showMore = root.querySelector('[data-atp-show-more]');
    if (!grid) return;
    empty(grid);

    if (!component) {
      if (breadcrumb) breadcrumb.textContent = state.data.manufacturer + ' / ' + (model ? model.title : '');
      if (heading) heading.textContent = 'No part types available';
      if (viewAll) viewAll.hidden = true;
      if (showMore) showMore.hidden = true;
      grid.appendChild(create('div', 'atp-emm-no-products', 'No part types are assigned to this engine model.'));
      return;
    }

    if (breadcrumb) breadcrumb.textContent = state.data.manufacturer + ' / ' + model.title;
    if (heading) heading.textContent = component.title;
    var allUrl = component.viewAllUrl || searchUrl(state.data.manufacturer, model.title, component.title);
    if (viewAll) {
      viewAll.href = allUrl;
      viewAll.hidden = false;
      var resultCount = (component.products || []).length;
      viewAll.textContent = resultCount ? 'View all ' + resultCount + ' results' : 'Search this part type';
    }

    var products = Array.isArray(component.products) ? component.products : [];
    if (!products.length) {
      grid.appendChild(create('div', 'atp-emm-no-products', 'No products are currently assigned to this part type.'));
      if (showMore) showMore.hidden = true;
      return;
    }

    renderProductList(grid, products.slice(0, state.limit), {
      currency: root.getAttribute('data-currency') || 'USD',
      placeholder: root.getAttribute('data-placeholder') || '',
      mobile: false
    });

    if (showMore) {
      var remaining = products.length - state.limit;
      showMore.hidden = remaining <= 0;
      showMore.textContent = remaining > 0 ? 'Show ' + Math.min(desktopPageSize, remaining) + ' more' : 'Show more';
    }
  }

  /* ------------------------------------------------------------------ */
  /* Mobile                                                              */
  /* ------------------------------------------------------------------ */
  function getMobileState(root) {
    var state = mobileStates.get(root);
    if (!state) {
      state = {
        stage: 'root',
        slug: '',
        label: '',
        source: '',
        data: null,
        modelIndex: 0,
        componentIndex: 0,
        limit: mobilePageSize
      };
      mobileStates.set(root, state);
    }
    return state;
  }

  function setMobileStatus(root, message, loading) {
    var status = root.querySelector('[data-atp-mobile-status]');
    var view = root.querySelector('[data-atp-mobile-view]');
    if (status) {
      status.hidden = false;
      status.classList.toggle('is-loading', !!loading);
      status.textContent = message || '';
    }
    if (view) empty(view);
  }

  function showMobileView(root) {
    var status = root.querySelector('[data-atp-mobile-status]');
    if (status) {
      status.hidden = true;
      status.classList.remove('is-loading');
    }
  }

  function setMobileTitles(root, kicker, heading) {
    var kickerNode = root.querySelector('[data-atp-mobile-kicker]');
    var headingNode = root.querySelector('[data-atp-mobile-heading]');
    if (kickerNode) kickerNode.textContent = kicker || 'Parts by engine';
    if (headingNode) headingNode.textContent = heading || '';
  }

  function openMobileManufacturer(button) {
    var root = button.closest('[data-atp-emm-mobile]');
    if (!root) return;
    var stack = root.closest('.atp-mobile-menu-stack');
    if (stack) {
      stack.scrollTop = 0;
      stack.classList.add('atp-mobile-menu-stack--panel-open');
    }
    var panel = root.querySelector('[data-atp-mobile-panel]');
    var state = getMobileState(root);
    state.slug = button.getAttribute('data-atp-mobile-manufacturer') || '';
    state.label = button.getAttribute('data-atp-label') || button.textContent.trim();
    state.source = button.getAttribute('data-atp-source') || '';
    state.data = null;
    state.stage = 'models';
    state.limit = mobilePageSize;
    mobileStates.set(root, state);
    if (panel) {
      panel.hidden = false;
      panel.scrollTop = 0;
    }
    setMobileTitles(root, 'Parts by engine', state.label);
    setMobileStatus(root, 'Loading ' + state.label + ' engine parts…', true);

    loadData(state.source).then(function (data) {
      var current = getMobileState(root);
      if (current.slug !== state.slug) return;
      current.data = data;
      current.modelIndex = firstModelIndex(data);
      current.componentIndex = firstComponentIndex(data.models[current.modelIndex]);
      current.stage = 'models';
      mobileStates.set(root, current);
      renderMobile(root);
    }).catch(function () {
      var current = getMobileState(root);
      if (current.slug !== state.slug) return;
      setMobileStatus(root, 'This menu could not be loaded. Please refresh the page or use site search.', false);
    });
  }

  function renderMobile(root) {
    var state = getMobileState(root);
    if (!state.data) return;
    showMobileView(root);
    if (state.stage === 'models') renderMobileModels(root, state);
    if (state.stage === 'components') renderMobileComponents(root, state);
    if (state.stage === 'products') renderMobileProducts(root, state);
    var panel = root.querySelector('[data-atp-mobile-panel]');
    if (panel) panel.scrollTop = 0;
  }

  function renderMobileModels(root, state) {
    var view = root.querySelector('[data-atp-mobile-view]');
    if (!view) return;
    empty(view);
    setMobileTitles(root, 'Choose an engine model', state.data.manufacturer);
    var list = create('ul', 'atp-emm-mobile-list');
    state.data.models.forEach(function (model, index) {
      var item = create('li');
      var button = create('button', 'atp-emm-mobile-list-button');
      button.type = 'button';
      button.setAttribute('data-atp-mobile-model', String(index));
      button.appendChild(create('span', '', model.title));
      var meta = create('span');
      meta.appendChild(create('span', 'atp-emm-count', String(countProducts(model))));
      meta.appendChild(create('span', 'atp-emm-mobile-arrow', '›'));
      button.appendChild(meta);
      item.appendChild(button);
      list.appendChild(item);
    });
    if (!state.data.models.length) list.appendChild(create('li', 'atp-emm-no-products', 'No engine models are available.'));
    view.appendChild(list);
  }

  function renderMobileComponents(root, state) {
    var view = root.querySelector('[data-atp-mobile-view]');
    var model = state.data.models[state.modelIndex];
    if (!view || !model) return;
    empty(view);
    setMobileTitles(root, state.data.manufacturer, model.title);
    var list = create('ul', 'atp-emm-mobile-list');
    (model.components || []).forEach(function (component, index) {
      var item = create('li');
      var button = create('button', 'atp-emm-mobile-list-button');
      button.type = 'button';
      button.setAttribute('data-atp-mobile-component', String(index));
      button.appendChild(create('span', '', component.title));
      var meta = create('span');
      meta.appendChild(create('span', 'atp-emm-count', String((component.products || []).length)));
      meta.appendChild(create('span', 'atp-emm-mobile-arrow', '›'));
      button.appendChild(meta);
      item.appendChild(button);
      list.appendChild(item);
    });
    if (!(model.components || []).length) list.appendChild(create('li', 'atp-emm-no-products', 'No part types are assigned to this model.'));
    view.appendChild(list);
  }

  function renderMobileProducts(root, state) {
    var view = root.querySelector('[data-atp-mobile-view]');
    var model = state.data.models[state.modelIndex];
    var component = model && model.components ? model.components[state.componentIndex] : null;
    if (!view || !model || !component) return;
    empty(view);
    setMobileTitles(root, model.title, component.title);

    var header = create('div', 'atp-emm-mobile-products-header');
    header.appendChild(create('p', '', state.data.manufacturer + ' / ' + model.title));
    var viewAll = create('a', '', 'View all results');
    viewAll.href = component.viewAllUrl || searchUrl(state.data.manufacturer, model.title, component.title);
    header.appendChild(viewAll);
    view.appendChild(header);

    var products = Array.isArray(component.products) ? component.products : [];
    var list = create('div', 'atp-emm-mobile-product-list');
    /* Append BEFORE rendering: the fetch queue skips cards that aren't in
       the DOM, so the list must be connected when jobs are queued. */
    view.appendChild(list);
    if (!products.length) {
      list.appendChild(create('div', 'atp-emm-no-products', 'No products are currently assigned to this part type.'));
    } else {
      renderProductList(list, products.slice(0, state.limit), {
        currency: root.getAttribute('data-currency') || 'USD',
        placeholder: root.getAttribute('data-placeholder') || '',
        mobile: true
      });
    }

    var remaining = products.length - state.limit;
    if (remaining > 0) {
      var more = create('button', 'atp-emm-show-more atp-emm-mobile-show-more', 'Show ' + Math.min(mobilePageSize, remaining) + ' more');
      more.type = 'button';
      more.setAttribute('data-atp-mobile-show-more', 'true');
      view.appendChild(more);
    }
  }

  function resetMobile(root) {
    if (!root) return;
    var panel = root.querySelector('[data-atp-mobile-panel]');
    if (panel) panel.hidden = true;
    var stack = root.closest('.atp-mobile-menu-stack');
    if (stack) stack.classList.remove('atp-mobile-menu-stack--panel-open');
    var state = getMobileState(root);
    state.stage = 'root';
    state.slug = '';
    state.label = '';
    state.source = '';
    mobileStates.set(root, state);
  }

  function mobileBack(root) {
    var state = getMobileState(root);
    if (state.stage === 'products') {
      state.stage = 'components';
      state.limit = mobilePageSize;
      mobileStates.set(root, state);
      renderMobile(root);
      return;
    }
    if (state.stage === 'components') {
      state.stage = 'models';
      mobileStates.set(root, state);
      renderMobile(root);
      return;
    }
    resetMobile(root);
  }

  /* ------------------------------------------------------------------ */
  /* Delegated events (bound once — safe across Theme Editor reloads)    */
  /* ------------------------------------------------------------------ */
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('.atp-emm-trigger');
    if (trigger) {
      event.preventDefault();
      var root = trigger.closest('[data-atp-emm-desktop]');
      if (root === activeDesktopRoot && trigger.classList.contains('is-active')) closeDesktop(root, false);
      else openDesktop(trigger);
      return;
    }

    var closeButton = event.target.closest('[data-atp-close]');
    if (closeButton) {
      event.preventDefault();
      closeDesktop(closeButton.closest('[data-atp-emm-desktop]'), true);
      return;
    }

    var modelButton = event.target.closest('[data-atp-model-index]');
    if (modelButton) {
      var modelRoot = modelButton.closest('[data-atp-emm-desktop]');
      var modelState = desktopStates.get(modelRoot);
      if (!modelState) return;
      modelState.modelIndex = Number(modelButton.getAttribute('data-atp-model-index')) || 0;
      modelState.componentIndex = firstComponentIndex(modelState.data.models[modelState.modelIndex]);
      modelState.filter = '';
      modelState.limit = desktopPageSize;
      var modelFilter = modelRoot.querySelector('[data-atp-filter]');
      if (modelFilter) modelFilter.value = '';
      desktopStates.set(modelRoot, modelState);
      renderDesktop(modelRoot);
      return;
    }

    var componentButton = event.target.closest('[data-atp-component-index]');
    if (componentButton) {
      var componentRoot = componentButton.closest('[data-atp-emm-desktop]');
      var componentState = desktopStates.get(componentRoot);
      if (!componentState) return;
      componentState.componentIndex = Number(componentButton.getAttribute('data-atp-component-index')) || 0;
      componentState.limit = desktopPageSize;
      desktopStates.set(componentRoot, componentState);
      renderDesktopComponents(componentRoot, componentState);
      renderDesktopProducts(componentRoot, componentState);
      return;
    }

    var showMore = event.target.closest('[data-atp-show-more]');
    if (showMore) {
      var moreRoot = showMore.closest('[data-atp-emm-desktop]');
      var moreState = desktopStates.get(moreRoot);
      if (!moreState) return;
      moreState.limit += desktopPageSize;
      desktopStates.set(moreRoot, moreState);
      renderDesktopProducts(moreRoot, moreState);
      return;
    }

    var mobileManufacturer = event.target.closest('[data-atp-mobile-manufacturer]');
    if (mobileManufacturer) {
      event.preventDefault();
      openMobileManufacturer(mobileManufacturer);
      return;
    }

    var mobileModel = event.target.closest('[data-atp-mobile-model]');
    if (mobileModel) {
      var mobileModelRoot = mobileModel.closest('[data-atp-emm-mobile]');
      var mobileModelState = getMobileState(mobileModelRoot);
      mobileModelState.modelIndex = Number(mobileModel.getAttribute('data-atp-mobile-model')) || 0;
      mobileModelState.componentIndex = firstComponentIndex(mobileModelState.data.models[mobileModelState.modelIndex]);
      mobileModelState.stage = 'components';
      mobileModelState.limit = mobilePageSize;
      mobileStates.set(mobileModelRoot, mobileModelState);
      renderMobile(mobileModelRoot);
      return;
    }

    var mobileComponent = event.target.closest('[data-atp-mobile-component]');
    if (mobileComponent) {
      var mobileComponentRoot = mobileComponent.closest('[data-atp-emm-mobile]');
      var mobileComponentState = getMobileState(mobileComponentRoot);
      mobileComponentState.componentIndex = Number(mobileComponent.getAttribute('data-atp-mobile-component')) || 0;
      mobileComponentState.stage = 'products';
      mobileComponentState.limit = mobilePageSize;
      mobileStates.set(mobileComponentRoot, mobileComponentState);
      renderMobile(mobileComponentRoot);
      return;
    }

    var mobileMore = event.target.closest('[data-atp-mobile-show-more]');
    if (mobileMore) {
      var mobileMoreRoot = mobileMore.closest('[data-atp-emm-mobile]');
      var mobileMoreState = getMobileState(mobileMoreRoot);
      mobileMoreState.limit += mobilePageSize;
      mobileStates.set(mobileMoreRoot, mobileMoreState);
      renderMobileProducts(mobileMoreRoot, mobileMoreState);
      return;
    }

    var mobileBackButton = event.target.closest('[data-atp-mobile-back]');
    if (mobileBackButton) {
      event.preventDefault();
      mobileBack(mobileBackButton.closest('[data-atp-emm-mobile]'));
      return;
    }

    /* Let the theme close its drawer normally, but reset this menu for the next open. */
    var nativeMobileClose = event.target.closest('.js-btn-close');
    if (nativeMobileClose) {
      var mobileDrawer = nativeMobileClose.closest('#mobile-drawer-main');
      if (mobileDrawer) {
        mobileDrawer.querySelectorAll('[data-atp-emm-mobile]').forEach(function (mobileRoot) {
          resetMobile(mobileRoot);
        });
      }
    }

    if (activeDesktopRoot && !event.target.closest('[data-atp-emm-desktop]')) closeDesktop(activeDesktopRoot, false);
  });

  document.addEventListener('input', function (event) {
    var filter = event.target.closest('[data-atp-filter]');
    if (!filter) return;
    var root = filter.closest('[data-atp-emm-desktop]');
    var state = desktopStates.get(root);
    if (!state) return;
    state.filter = filter.value || '';
    var entries = filteredComponents(state);
    if (entries.length && !entries.some(function (entry) { return entry.index === state.componentIndex; })) {
      state.componentIndex = entries[0].index;
      state.limit = desktopPageSize;
    }
    desktopStates.set(root, state);
    renderDesktopComponents(root, state);
    renderDesktopProducts(root, state);
  });

  document.addEventListener('keydown', function (event) {
    var hamburger = event.target.closest('.js-main-drawer-open-button');
    if (hamburger && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      hamburger.click();
      return;
    }

    var trigger = event.target.closest('.atp-emm-trigger');
    if (trigger) {
      var triggers = Array.prototype.slice.call(trigger.closest('[data-atp-emm-desktop]').querySelectorAll('.atp-emm-trigger'));
      var index = triggers.indexOf(trigger);
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        /* Toggle for parity with mouse clicks. */
        if (trigger.classList.contains('is-active')) closeDesktop(trigger.closest('[data-atp-emm-desktop]'), false);
        else openDesktop(trigger);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        triggers[(index + 1) % triggers.length].focus();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        triggers[(index - 1 + triggers.length) % triggers.length].focus();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        closeDesktop(trigger.closest('[data-atp-emm-desktop]'), true);
      }
      return;
    }

    if (event.key === 'Escape' && activeDesktopRoot) {
      event.preventDefault();
      closeDesktop(activeDesktopRoot, true);
    }
  });

  /* Desktop engine menus intentionally open only by click or keyboard activation. */

  window.addEventListener('resize', function () {
    if (activeDesktopRoot) positionDesktop(activeDesktopRoot);
  }, { passive: true });

  window.addEventListener('scroll', function () {
    if (activeDesktopRoot) closeDesktop(activeDesktopRoot, false);
  }, { passive: true });

  /* Theme Editor: sections get re-rendered; old nodes (and their WeakMap
     state) are discarded. Just make sure nothing stays "open". */
  document.addEventListener('shopify:section:load', function () {
    activeDesktopRoot = null;
    document.documentElement.classList.remove('atp-emm-is-open');
  });
})();
